import { useState, useEffect } from 'react';
import { paymentsAPI, bankingAPI } from '../../api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';

/**
 * Reusable payment form for recording manual payments against an invoice.
 * Used in InvoiceEditor and Invoices list (per-invoice payment modal).
 *
 * @param {string} invoiceId - Invoice UUID
 * @param {number} balanceDue - Current balance due on the invoice
 * @param {function} onSuccess - Callback after successful payment
 * @param {function} onCancel - Callback when user cancels
 */
const InvoicePaymentForm = ({ invoiceId, balanceDue, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    bank_account_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'manual',
    reference: '',
    notes: '',
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const accounts = await bankingAPI.getAccounts({ include_inactive: false });
      const accountsArray = Array.isArray(accounts) ? accounts : [];
      setBankAccounts(accountsArray);
      if (accountsArray.length > 0) {
        setFormData(prev => ({ ...prev, bank_account_id: accountsArray[0].id }));
      } else {
        setError('No bank accounts found. Please create a bank account first.');
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
      setError('Failed to load bank accounts. Please try again.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.bank_account_id) {
      setError('Please select a bank account');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    const paymentAmount = parseFloat(formData.amount);
    if (paymentAmount > balanceDue) {
      setError(`Payment amount cannot exceed balance due of ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(balanceDue)}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentData = {
        invoice_id: invoiceId,
        bank_account_id: formData.bank_account_id,
        amount: paymentAmount.toString(),
        payment_date: formData.payment_date || new Date().toISOString().split('T')[0],
        payment_method: formData.payment_method || 'manual',
        ...(formData.reference && formData.reference.trim() ? { reference: formData.reference.trim() } : {}),
        ...(formData.notes && formData.notes.trim() ? { notes: formData.notes.trim() } : {}),
      };

      await paymentsAPI.create(paymentData);
      onSuccess();
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to record payment';

      if (errorData) {
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const firstError = errorData.errors[0];
          errorMessage = `${firstError.field || 'Field'}: ${firstError.message || errorData.error || 'Invalid value'}`;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error('Record payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Balance Due Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>Balance Due:</strong> {formatCurrency(balanceDue)}
        </p>
      </div>

      {/* Grid Layout for Key Fields */}
      <div className="grid grid-cols-2 gap-4">
        {/* Bank Account - Required */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bank Account <span className="text-red-500">*</span>
          </label>
          {loadingAccounts ? (
            <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400">
              Loading bank accounts...
            </div>
          ) : bankAccounts.length === 0 ? (
            <div className="w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              No bank accounts available. Please create a bank account first.
            </div>
          ) : (
            <Select
              name="bank_account_id"
              value={formData.bank_account_id}
              onChange={handleChange}
              required
            >
              <option value="">Select bank account</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.account_name} {account.account_type && `(${account.account_type})`}
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Payment Amount - Required */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Payment Amount <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            min="0.01"
            step="0.01"
            max={balanceDue}
            placeholder="0.00"
          />
          <div className="mt-2 flex items-center">
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, amount: balanceDue.toFixed(2) }));
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium underline"
            >
              Pay in full
            </button>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({formatCurrency(balanceDue)} remaining)
            </span>
          </div>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Payment Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Payment Date
          </label>
          <Input
            type="date"
            name="payment_date"
            value={formData.payment_date}
            onChange={handleChange}
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Payment Method
          </label>
          <Select
            name="payment_method"
            value={formData.payment_method}
            onChange={handleChange}
          >
            <option value="manual">Manual</option>
            <option value="bank_transfer">Bank Transfer (BACS)</option>
            <option value="card">Credit/Debit Card</option>
            <option value="cash">Cash</option>
            <option value="cheque">Cheque</option>
            <option value="stripe">Stripe</option>
          </Select>
        </div>
      </div>

      {/* Reference */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Reference (Optional)
        </label>
        <Input
          type="text"
          name="reference"
          value={formData.reference}
          onChange={handleChange}
          placeholder="Payment reference or transaction ID"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Notes (Optional)
        </label>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Additional payment notes..."
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
        >
          {loading ? 'Recording Payment...' : 'Record Payment'}
        </Button>
      </div>
    </form>
  );
};

export default InvoicePaymentForm;
